/**
 * AIDIA vault — the single source of truth for the client.
 *
 * Shape of the design:
 *   • Every record is encrypted individually, so sync moves only what changed.
 *   • localStorage keeps the encrypted mirror, so the app opens instantly and
 *     keeps working when the backend is unreachable.
 *   • PostgreSQL (via the backend) is the durable store and the meeting point
 *     for multiple devices. It only ever receives ciphertext.
 *
 * Nothing here decrypts anything until the master password has produced a key.
 */

import * as Crypto from "./crypto.js";
import * as api from "./api.js";

const META_KEY = "aidia.meta";
const RECORDS_KEY = "aidia.records";
const CURSOR_KEY = "aidia.cursor";
const NOTIFIED_KEY = "aidia.notified";
const PENDING_KEY = "aidia.pending";

export const KINDS = ["entry", "transaction", "goal", "account", "setting"];

// Well-known ids for the singleton 'setting' records.
export const SETTING_IDS = {
  preferences: "setting:preferences",
  categoryRules: "setting:category-rules",
  categories: "setting:categories",
};

// --- in-memory state ------------------------------------------------------
let records = new Map(); // id -> { id, kind, data, updatedAt, deletedAt }
let envelopes = new Map(); // id -> { id, kind, iv, ciphertext, updatedAt, deletedAt }
let pending = new Set(); // ids changed locally but not yet accepted by the server
const listeners = new Set();

export const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const emit = () => listeners.forEach((fn) => fn());

// --- local persistence ----------------------------------------------------
const readJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};
const writeJson = (key, value) => localStorage.setItem(key, JSON.stringify(value));

export const loadMeta = () => readJson(META_KEY, null);
export const hasVault = () => loadMeta() !== null;
const saveMeta = (meta) => writeJson(META_KEY, meta);

const saveEnvelopes = () => writeJson(RECORDS_KEY, [...envelopes.values()]);
const savePending = () => writeJson(PENDING_KEY, [...pending]);

export const getCursor = () => Number(localStorage.getItem(CURSOR_KEY) || 0);
const setCursor = (rev) => localStorage.setItem(CURSOR_KEY, String(rev));

// --- lifecycle ------------------------------------------------------------

/**
 * First run. Derives the key, mints a Sync ID + token for this vault, and
 * registers the identity with the backend so another device can join later.
 */
export async function createVault(password, registrationToken = "") {
  const { salt, verifier } = await Crypto.setup(password);
  const syncId = Crypto.randomId(16);
  const token = Crypto.randomId(24);

  const meta = { syncId, token, salt, verifier, createdAt: Date.now() };
  saveMeta(meta);

  records = new Map();
  envelopes = new Map();
  pending = new Set();
  saveEnvelopes();
  savePending();
  setCursor(0);

  // Best effort: a vault that can't reach the server still works locally and
  // registers itself on the next successful sync.
  try {
    await api.createVault({ syncId, salt, verifier, token, registrationToken });
  } catch (err) {
    // A closed server is a hard stop — the vault would never sync, and the
    // user should know now rather than discover it later.
    if (err.status === 403) {
      resetVault();
      throw new Error(err.message);
    }
    console.warn("[vault] server registration deferred:", err.message);
  }

  emit();
  return meta;
}

/** Returning user. Verifies the password, then decrypts the local mirror. */
export async function unlockVault(password) {
  const meta = loadMeta();
  if (!meta) return false;
  if (!(await Crypto.unlock(password, meta.salt, meta.verifier))) return false;
  await hydrate();
  return true;
}

/** New device joining an existing vault: adopt its identity, then pull. */
export async function joinVault({ syncId, token, password }) {
  const remote = await api.getVault(syncId, token);
  if (!remote?.salt || !remote?.verifier) {
    throw new Error("That vault has no stored identity yet.");
  }
  if (!(await Crypto.unlock(password, remote.salt, remote.verifier))) {
    throw new Error("That master password does not match this vault.");
  }

  saveMeta({ syncId, token, salt: remote.salt, verifier: remote.verifier, joinedAt: Date.now() });
  records = new Map();
  envelopes = new Map();
  pending = new Set();
  saveEnvelopes();
  savePending();
  setCursor(0);

  await sync();
  return true;
}

export function lockVault() {
  Crypto.lock();
  records = new Map();
  envelopes = new Map();
  emit();
}

export const isUnlocked = () => Crypto.isUnlocked();

/** Verify the master password without disturbing the unlocked key. */
export async function verifyPassword(password) {
  const meta = loadMeta();
  if (!meta) return false;
  return Crypto.verify(password, meta.salt, meta.verifier);
}

/** Wipe this device. Does not touch the server copy. */
export function resetVault() {
  [META_KEY, RECORDS_KEY, CURSOR_KEY, NOTIFIED_KEY, PENDING_KEY].forEach((k) =>
    localStorage.removeItem(k)
  );
  records = new Map();
  envelopes = new Map();
  pending = new Set();
  Crypto.lock();
  emit();
}

/** Decrypt the local mirror into memory. */
async function hydrate() {
  envelopes = new Map(readJson(RECORDS_KEY, []).map((e) => [e.id, e]));
  pending = new Set(readJson(PENDING_KEY, []));
  records = new Map();

  for (const env of envelopes.values()) {
    if (env.deletedAt) continue; // tombstone — nothing to show
    try {
      const data = await Crypto.decrypt({ iv: env.iv, data: env.ciphertext });
      records.set(env.id, {
        id: env.id,
        kind: env.kind,
        data,
        updatedAt: env.updatedAt,
        deletedAt: env.deletedAt ?? null,
      });
    } catch {
      // A record encrypted under a different key (e.g. a stale mirror after a
      // restore) is skipped rather than taking the whole vault down.
      console.warn("[vault] could not decrypt record", env.id);
    }
  }
  emit();
}

// --- reads ----------------------------------------------------------------
export const allOfKind = (kind) =>
  [...records.values()].filter((r) => r.kind === kind && !r.deletedAt).map((r) => ({ id: r.id, ...r.data }));

export const getRecord = (id) => {
  const r = records.get(id);
  return r && !r.deletedAt ? { id: r.id, ...r.data } : null;
};

export const entries = () => allOfKind("entry");
export const transactions = () => allOfKind("transaction");
export const accounts = () => allOfKind("account");
export const goals = () => allOfKind("goal");

/** A 'setting' record, or the supplied fallback when it has never been written. */
export const setting = (id, fallback = {}) => {
  const r = records.get(id);
  return r && !r.deletedAt ? r.data : fallback;
};

// --- writes ---------------------------------------------------------------
const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

/**
 * Create or replace a record. Writes through to the encrypted mirror straight
 * away and queues the ciphertext for the server.
 */
export async function put(kind, value, id = null) {
  if (!KINDS.includes(kind)) throw new Error(`Unknown record kind: ${kind}`);
  if (!Crypto.isUnlocked()) throw new Error("Vault is locked");

  const recordId = id || value.id || uid();
  const { id: _drop, ...payload } = value;
  const updatedAt = Date.now();

  const blob = await Crypto.encrypt(payload);
  envelopes.set(recordId, {
    id: recordId,
    kind,
    iv: blob.iv,
    ciphertext: blob.data,
    updatedAt,
    deletedAt: null,
  });
  records.set(recordId, { id: recordId, kind, data: payload, updatedAt, deletedAt: null });

  pending.add(recordId);
  saveEnvelopes();
  savePending();
  emit();

  queueSync();
  return recordId;
}

/**
 * Delete. Keeps a tombstone (an encrypted empty payload) so the delete reaches
 * other devices instead of being resurrected by their copy.
 */
export async function remove(id) {
  const existing = envelopes.get(id);
  if (!existing) return;
  const deletedAt = Date.now();
  const blob = await Crypto.encrypt({});

  envelopes.set(id, {
    id,
    kind: existing.kind,
    iv: blob.iv,
    ciphertext: blob.data,
    updatedAt: deletedAt,
    deletedAt,
  });
  records.delete(id);

  pending.add(id);
  saveEnvelopes();
  savePending();
  emit();

  queueSync();
}

/** Merge a patch into a 'setting' singleton. */
export async function patchSetting(id, patch) {
  const current = setting(id, {});
  return put("setting", { ...current, ...patch }, id);
}

// --- sync -----------------------------------------------------------------
let syncTimer = null;
let syncing = false;
let syncState = { status: "idle", at: null, error: null };

export const getSyncState = () => ({ ...syncState, pending: pending.size });

const setSyncState = (patch) => {
  syncState = { ...syncState, ...patch };
  emit();
};

/** Coalesce the edits of a busy minute into one round trip. */
function queueSync() {
  clearTimeout(syncTimer);
  syncTimer = setTimeout(() => sync().catch(() => {}), 1200);
}

export async function sync() {
  const meta = loadMeta();
  if (!meta || !Crypto.isUnlocked()) return;
  if (syncing) return;
  syncing = true;
  setSyncState({ status: "syncing", error: null });

  try {
    // The identity may not have reached the server yet (created offline).
    try {
      await api.createVault({
        syncId: meta.syncId,
        salt: meta.salt,
        verifier: meta.verifier,
        token: meta.token,
      });
    } catch (err) {
      if (err.status !== 409) throw err; // 409 = someone else owns that id
    }

    // 1. Pull everything new, in rev order, paging until caught up.
    let cursor = getCursor();
    let guard = 0;
    for (;;) {
      const page = await api.pullRecords(meta.syncId, meta.token, cursor);
      if (page.records.length) await mergeRemote(page.records);
      cursor = page.cursor;
      setCursor(cursor);
      if (!page.hasMore || ++guard > 50) break;
    }

    // 2. Push whatever this device changed.
    const outgoing = [...pending]
      .map((id) => envelopes.get(id))
      .filter(Boolean)
      .map((e) => ({
        id: e.id,
        kind: e.kind,
        iv: e.iv,
        ciphertext: e.ciphertext,
        updatedAt: e.updatedAt,
        deletedAt: e.deletedAt,
      }));

    if (outgoing.length) {
      const res = await api.pushRecords(meta.syncId, meta.token, outgoing);
      pending.clear();
      savePending();
      // Our own writes come back on the next pull; skip past them.
      if (res.head) setCursor(res.head);
    }

    setSyncState({ status: "ok", at: Date.now(), error: null });
  } catch (err) {
    // Offline is a normal state for this app, not a failure to shout about.
    setSyncState({ status: err.status === 0 ? "offline" : "error", error: err.message });
  } finally {
    syncing = false;
    emit();
  }
}

/** Apply pulled records: newest edit wins, and a local pending edit is kept. */
async function mergeRemote(remote) {
  let changed = false;

  for (const r of remote) {
    const local = envelopes.get(r.id);

    // Don't let a pull overwrite an edit we haven't managed to push yet.
    if (pending.has(r.id) && local && local.updatedAt >= r.updatedAt) continue;
    if (local && local.updatedAt > r.updatedAt) continue;

    envelopes.set(r.id, {
      id: r.id,
      kind: r.kind,
      iv: r.iv,
      ciphertext: r.ciphertext,
      updatedAt: r.updatedAt,
      deletedAt: r.deletedAt,
    });

    if (r.deletedAt) {
      records.delete(r.id);
    } else {
      try {
        const data = await Crypto.decrypt({ iv: r.iv, data: r.ciphertext });
        records.set(r.id, {
          id: r.id,
          kind: r.kind,
          data,
          updatedAt: r.updatedAt,
          deletedAt: null,
        });
      } catch {
        console.warn("[vault] pulled a record this key cannot open:", r.id);
      }
    }
    changed = true;
  }

  if (changed) {
    saveEnvelopes();
    emit();
  }
}

// --- backup ---------------------------------------------------------------
export function exportBackup() {
  const meta = loadMeta();
  if (!meta) throw new Error("No vault to export.");
  return {
    app: "AIDIA",
    format: "aidia-backup",
    version: 2,
    exportedAt: new Date().toISOString(),
    // The sync token is deliberately excluded — a backup file should not hand
    // someone access to the live server copy.
    meta: { salt: meta.salt, verifier: meta.verifier, syncId: meta.syncId },
    records: [...envelopes.values()],
  };
}

export async function importBackup(obj) {
  if (!obj || obj.app !== "AIDIA" || obj.format !== "aidia-backup") {
    throw new Error("That is not an AIDIA backup file.");
  }
  if (!obj.meta?.salt || !obj.meta?.verifier) throw new Error("Backup is missing its vault identity.");

  const existing = loadMeta();
  saveMeta({
    syncId: obj.meta.syncId || Crypto.randomId(16),
    // A restored backup keeps talking to the same server record only if this
    // device still holds the token; otherwise it becomes a local-only vault
    // until the user pastes the token in Settings.
    token: existing?.syncId === obj.meta.syncId ? existing.token : "",
    salt: obj.meta.salt,
    verifier: obj.meta.verifier,
    restoredAt: Date.now(),
  });

  writeJson(RECORDS_KEY, obj.records || []);
  writeJson(PENDING_KEY, (obj.records || []).map((r) => r.id));
  setCursor(0);
  localStorage.removeItem(NOTIFIED_KEY);

  // The in-memory key belongs to the old vault — force a fresh unlock.
  lockVault();
}

/** Re-point this device at a vault on the server (Settings → sync). */
export function setSyncCredentials({ syncId, token }) {
  const meta = loadMeta();
  if (!meta) throw new Error("No vault on this device.");
  saveMeta({ ...meta, syncId, token });
}

// --- alert bookkeeping ----------------------------------------------------
export const getNotified = () => readJson(NOTIFIED_KEY, []);
export function markNotified(id) {
  const list = getNotified();
  if (!list.includes(id)) writeJson(NOTIFIED_KEY, [...list, id]);
}
